import { FC, useState } from "react";
import { useDispatch } from "react-redux";
import { BookingRequestDetails } from "../../redux/reducers/Properties/PropertiesSliceTypes";
import { AppDispatch } from "redux/store";
import {
  getBookingDetails,
  sendBookingInvoice,
} from "../../redux/reducers/Properties/PropertiesSlice";
import { currencySymbol } from "shared/CurrencySymbol";
import successHandler from "utils/helpers/SuccessHandler";
import moment from "moment";
import { Drawer } from "@mantine/core";

interface InvoiceProps {
  bookingRequestDetails: BookingRequestDetails | null;
  closeInvoice: () => void;
  callback?: () => void;
  fetchMessages?: (pge: number) => Promise<void>;
}

interface Charge {
  name: string;
  value: number;
}

// Accepts ISO dates and the pre-formatted text the bookings list sends ("January 05, 2027").
const formatDate = (value: unknown): string => {
  if (!value) return "";
  const parsed = moment.utc(
    value as string,
    [moment.ISO_8601, "MMMM DD, YYYY"],
    true
  );
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : String(value);
};

const inputClass =
  "w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2.5 text-base sm:text-sm text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#f5694b]/40 focus:border-[#f5694b]";

const Invoice: FC<InvoiceProps> = ({
  bookingRequestDetails,
  closeInvoice,
  callback,
  fetchMessages,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const b: any = bookingRequestDetails || {};

  const [expenses, setExpenses] = useState<Charge[]>([]);
  const [discounts, setDiscounts] = useState<number[]>([]);
  const [openForm, setOpenForm] = useState<"charge" | "discount" | null>(null);
  const [expenseName, setExpenseName] = useState("");
  const [expenseValue, setExpenseValue] = useState("");
  const [discountValue, setDiscountValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const money = (amount: number) =>
    `${currencySymbol()}${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;

  // Amounts from the booking
  const rent = Number(b.item_total) || 0;
  const serviceFee = Number(b.service_tax) || 0;
  const extraGuests = Number(b.extra_guest_amount) || 0;
  const taxTotal = Number(b.tax_total) || 0;
  const baseTotal =
    Number(b.total) || rent + serviceFee + extraGuests + taxTotal;

  const totalExpenses = expenses.reduce((sum, e) => sum + e.value, 0);
  const totalDiscounts = discounts.reduce((sum, d) => sum + d, 0);
  const grandTotal = baseTotal + totalExpenses - totalDiscounts;
  const hostPayout = grandTotal - serviceFee;
  const invalidTotal = grandTotal <= 0;

  const addExpense = () => {
    const value = parseFloat(expenseValue);
    if (!expenseName.trim() || !(value > 0)) return;
    setExpenses([...expenses, { name: expenseName.trim(), value }]);
    setExpenseName("");
    setExpenseValue("");
  };

  const addDiscount = () => {
    const value = parseFloat(discountValue);
    if (!(value > 0)) return;
    setDiscounts([...discounts, value]);
    setDiscountValue("");
  };

  const onAmountChange = (setter: (v: string) => void) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (/^\d*\.?\d*$/.test(e.target.value)) setter(e.target.value);
  };

  const sendInvoiceHandler = async () => {
    if (invalidTotal) return;
    setIsLoading(true);
    setError("");
    try {
      const payloadData = {
        booking_id: bookingRequestDetails?.id,
        booking: {
          status: "invoice_sent",
          invoice_attributes: {
            status: "sent",
            billing_type: "one time",
            invoice_type: "Reservation fee",
            expenses_attributes: expenses.map((x) => ({
              category: x.name,
              amount: x.value,
            })),
            discounts_attributes: discounts.map((y) => ({
              description: "Seasonal discount",
              amount: y,
            })),
          },
        },
      };

      const action: any = await dispatch(sendBookingInvoice(payloadData));
      if (sendBookingInvoice.fulfilled.match(action) && action.payload?.success) {
        await dispatch(getBookingDetails(bookingRequestDetails?.id));
        if (fetchMessages) {
          await fetchMessages(1);
        }
        successHandler("Invoice sent to the guest.", 4000);
        setIsLoading(false);
        closeInvoice();
        if (callback) callback();
        return;
      }
      setError(
        action?.payload?.message ||
          action?.payload?.error ||
          "We couldn't send the invoice. Please try again."
      );
    } catch (err) {
      console.error("Error in sendInvoiceHandler:", err);
      setError("We couldn't send the invoice. Please try again.");
    }
    setIsLoading(false);
  };

  const guestFirstName = b.request_by ? String(b.request_by).split(" ")[0] : "";
  const subtitle = [guestFirstName, b.booked_room, b.property?.title, b.number]
    .filter(Boolean)
    .join(" · ");

  const months = Number(b.no_of_months) || 1;
  const period = [formatDate(b.check_in_date), formatDate(b.check_out_date)]
    .filter(Boolean)
    .join(" to ");

  const rowClass =
    "flex items-center justify-between py-2.5 text-sm border-b border-neutral-100 dark:border-neutral-800";
  const removeBtn =
    "ml-2 w-9 h-9 sm:w-7 sm:h-7 inline-flex items-center justify-center rounded-full text-neutral-400 hover:text-red-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors";

  return (
    <Drawer
      opened={true}
      onClose={closeInvoice}
      position="right"
      size="min(480px, 100vw)"
      withCloseButton={false}
      overlayProps={{ backgroundOpacity: 0.6, blur: 2 }}
      classNames={{
        content:
          "!bg-white dark:!bg-neutral-900 border-l border-neutral-200 dark:border-neutral-700",
        body: "!p-0",
      }}
    >
      <div
        className="flex flex-col text-neutral-900 dark:text-neutral-100"
        style={{ height: "100dvh" }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-neutral-100 dark:border-neutral-800 shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold leading-tight">Create invoice</h2>
            {subtitle && (
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">
                {subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={closeInvoice}
            disabled={isLoading}
            className="w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-full text-xl text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-50"
          >
            ×
          </button>
        </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* Summary */}
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: "Period", value: period || "-", wide: true },
            { label: "Duration", value: `${months} ${months === 1 ? "month" : "months"}` },
            { label: "Guests", value: String(b.number_of_guests ?? "-") },
          ].map((item) => (
            <div
              key={item.label}
              className={`rounded-lg bg-neutral-50 dark:bg-neutral-800 px-3 py-2 ${
                item.wide ? "col-span-2" : ""
              }`}
            >
              <p className="text-[11px] text-neutral-400">{item.label}</p>
              <p className="text-sm font-medium leading-snug">{item.value}</p>
            </div>
          ))}
        </div>

        {/* Line items */}
        <div>
          <div className={rowClass}>
            <span>First month's rent</span>
            <span className="font-medium">{money(rent)}</span>
          </div>
          {serviceFee > 0 && (
            <div className={rowClass}>
              <span>Service fee</span>
              <span className="font-medium">{money(serviceFee)}</span>
            </div>
          )}
          {extraGuests > 0 && (
            <div className={rowClass}>
              <span>Extra guests</span>
              <span className="font-medium">{money(extraGuests)}</span>
            </div>
          )}
          {taxTotal > 0 && (
            <div className={rowClass}>
              <span>Tax</span>
              <span className="font-medium">{money(taxTotal)}</span>
            </div>
          )}

          {expenses.map((expense, index) => (
            <div key={`expense-${index}`} className={rowClass}>
              <span className="truncate pr-2" title={expense.name}>
                {expense.name}
              </span>
              <span className="whitespace-nowrap">
                <span className="font-medium">{money(expense.value)}</span>
                <button
                  type="button"
                  aria-label={`Remove ${expense.name}`}
                  className={removeBtn}
                  onClick={() =>
                    setExpenses(expenses.filter((_, i) => i !== index))
                  }
                >
                  ×
                </button>
              </span>
            </div>
          ))}

          {discounts.map((discount, index) => (
            <div key={`discount-${index}`} className={rowClass}>
              <span>Discount</span>
              <span className="whitespace-nowrap">
                <span className="font-medium text-green-600 dark:text-green-400">
                  − {money(discount)}
                </span>
                <button
                  type="button"
                  aria-label="Remove discount"
                  className={removeBtn}
                  onClick={() =>
                    setDiscounts(discounts.filter((_, i) => i !== index))
                  }
                >
                  ×
                </button>
              </span>
            </div>
          ))}
        </div>

        {/* Add charge / discount */}
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setOpenForm(openForm === "charge" ? null : "charge")}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                openForm === "charge"
                  ? "border-[#f5694b] text-[#f5694b]"
                  : "border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-800"
              }`}
            >
              + Add charge
            </button>
            <button
              type="button"
              onClick={() => setOpenForm(openForm === "discount" ? null : "discount")}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                openForm === "discount"
                  ? "border-green-500 text-green-600 dark:text-green-400"
                  : "border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-800"
              }`}
            >
              + Add discount
            </button>
          </div>

          {openForm === "charge" && (
            <div className="flex flex-wrap sm:flex-nowrap gap-2">
              <input
                type="text"
                value={expenseName}
                onChange={(e) => setExpenseName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addExpense()}
                placeholder="Charge name"
                className={inputClass}
              />
              <div className="relative w-full sm:w-36">
                <span className="absolute inset-y-0 left-3 flex items-center text-sm text-neutral-400">
                  {currencySymbol()}
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={expenseValue}
                  onChange={onAmountChange(setExpenseValue)}
                  onKeyDown={(e) => e.key === "Enter" && addExpense()}
                  placeholder="Amount"
                  className={`${inputClass} pl-7`}
                />
              </div>
              <button
                type="button"
                onClick={addExpense}
                disabled={!expenseName.trim() || !(parseFloat(expenseValue) > 0)}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-[#f5694b] hover:bg-[#e55838] disabled:bg-neutral-300 dark:disabled:bg-neutral-700 disabled:cursor-not-allowed transition-colors"
              >
                Add
              </button>
            </div>
          )}

          {openForm === "discount" && (
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute inset-y-0 left-3 flex items-center text-sm text-neutral-400">
                  {currencySymbol()}
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={discountValue}
                  onChange={onAmountChange(setDiscountValue)}
                  onKeyDown={(e) => e.key === "Enter" && addDiscount()}
                  placeholder="Discount amount"
                  className={`${inputClass} pl-7`}
                />
              </div>
              <button
                type="button"
                onClick={addDiscount}
                disabled={!(parseFloat(discountValue) > 0)}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-neutral-300 dark:disabled:bg-neutral-700 disabled:cursor-not-allowed transition-colors"
              >
                Add
              </button>
            </div>
          )}
        </div>

        {/* Totals */}
        <div>
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-medium">Total charged to guest</span>
            <span className="text-2xl font-semibold text-[#f5694b]">
              {money(grandTotal)}
            </span>
          </div>
          <div className="mt-3 rounded-xl bg-green-50 dark:bg-green-900/20 px-4 py-3 text-sm text-green-800 dark:text-green-300 space-y-1">
            {serviceFee > 0 && (
              <div className="flex justify-between">
                <span>Nomadroof service fee</span>
                <span>− {money(serviceFee)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold">
              <span>You receive</span>
              <span>{money(hostPayout)}</span>
            </div>
          </div>
        </div>

        {invalidTotal && (
          <p className="text-xs text-red-600 dark:text-red-400">
            Discounts can't be larger than the total.
          </p>
        )}
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300"
          >
            {error}
          </div>
        )}

        <p className="text-xs text-neutral-400">
          The guest is notified and can pay once you send.
        </p>
      </div>

      {/* Footer (always visible) */}
      <div
        className="shrink-0 flex items-center justify-end gap-3 px-5 pt-4 border-t border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900"
        style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
      >
        <div className="flex gap-2 ml-auto">
          <button
            type="button"
            onClick={closeInvoice}
            disabled={isLoading}
            className="px-4 py-2.5 rounded-lg text-sm border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={sendInvoiceHandler}
            disabled={isLoading || invalidTotal}
            className="px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-[#f5694b] hover:bg-[#e55838] active:bg-[#d54828] disabled:bg-neutral-300 dark:disabled:bg-neutral-700 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? "Sending..." : "Send invoice"}
          </button>
        </div>
      </div>
      </div>
    </Drawer>
  );
};

export default Invoice;